'use client';

import { useEffect, useState } from 'react';
import { Icon } from '../../../components/icons';
import { Link } from '../../../components/ui/Link';
import { buttonVariants } from '../../../components/ui/button-variants';
import { ROUTES, adoptShelterQuizPath } from '../../../constants/routes';
import { cn } from '../../../lib/utils';
import {
  fetchPublicSheltersList,
  type PublicShelterListItemDto,
} from '../api/publicSheltersApi';

export function AdoptSheltersDirectoryPage() {
  const [shelters, setShelters] = useState<PublicShelterListItemDto[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicSheltersList()
      .then((data) => {
        if (!cancelled) setShelters(data.shelters);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load shelters. Check your connection and API URL.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className='mx-auto max-w-3xl space-y-8 pb-12 pt-4 sm:pt-6'>
      <Link
        href={ROUTES.home}
        className={cn(
          buttonVariants({ variant: 'outlineHighlight', size: 'sm' }),
          'no-underline',
        )}
      >
        <Icon name='arrow-left' className='size-4' />
        Back
      </Link>

      <header className='space-y-2'>
        <h1 className='font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl'>
          Find a shelter
        </h1>
        <p className='text-sm text-muted-foreground sm:text-base'>
          Choose a shelter to take the adoption match quiz for pets available
          there.
        </p>
      </header>

      {error ? (
        <p
          className='rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900'
          role='alert'
        >
          {error}
        </p>
      ) : null}

      {!error && shelters === null ? (
        <p className='text-sm text-muted-foreground' role='status'>
          Loading shelters…
        </p>
      ) : null}

      {!error && shelters && shelters.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No active shelters are available right now. Check back later.
        </p>
      ) : null}

      {!error && shelters && shelters.length > 0 ? (
        <ul className='divide-y divide-border rounded-xl border border-border bg-card'>
          {shelters.map((s) => {
            const href = adoptShelterQuizPath(s.slug ?? s.id);
            return (
              <li key={s.id}>
                <Link
                  href={href}
                  className='block px-4 py-4 text-base font-medium text-foreground no-underline transition-colors hover:bg-accent/40 sm:px-5'
                >
                  {s.name}
                  {s.slug ? (
                    <span className='mt-0.5 block text-xs font-normal text-muted-foreground'>
                      /adopt/{s.slug}
                    </span>
                  ) : null}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
